import { Component, Input, OnChanges } from '@angular/core';
import { BRAND } from '../../../data/brand/brand.data';
import { Product, ProductLine, isThemeFree } from '../../../model/product/product.model';
import { posterColorSet } from '../line-color-set';

/**
 * A single Pinterest-ready pin for one theme, a SELF-CONTAINED copy of the
 * themes-page poster theme card design (wallpaper + scrim + floating logo +
 * CATEGORY, APPEARANCE / name / tags identity stack), re-sized to Pinterest's
 * 2:3 pin ratio (1000x1500) and scaled up so a clean screenshot of one card
 * drops straight onto a board. Static only: no hover, no cursor, no click,  * it exists purely to be captured.
 *
 * Driven by its `product` input.
 */
@Component( {
  selector: 'app-pinterest-card',
  templateUrl: './pinterest-card.component.html',
  styleUrls: [ './pinterest-card.component.scss' ],
} )
export class PinterestCardComponent implements OnChanges {

  /** The theme this pin renders. */
  @Input() product!: Product;

  /** Owning product line (kept for API parity; unused in this layout). */
  @Input() productLine?: ProductLine;

  BRAND = BRAND;

  /** Watermark printed on the image so pins stay attributable. */
  readonly siteUrl = 'codigrate.com';

  /** The five colour-tag swatches (the site's colour circles). */
  private readonly MAX_COLORS = 5;

  // Derived view state, recomputed whenever the inputs change.
  colors: string[] = [];
  background = '';
  category = '';
  appearance = '';
  tags: string[] = [];
  platformLogos: string[] = [];
  editorialLabel = '';
  isFree = false;
  isDark = false;
  screenshots: string[] = [];

  // Poster theme-card view-state (identical recipe to theme-card.component's
  // poster variant): scrim panel tone, readable foregrounds, quiet dotted tag
  // line, so the pin IS the poster card, just at the 2:3 pin size.
  posterPanel = '#17181a';
  posterPanel0 = '#17181a00';
  posterFg = '#ffffff';
  posterFgDim = '#9b9ba0';
  posterTags = '';
  plainDescription = '';

  ngOnChanges(): void {
    const product = this.product;
    if ( !product ) {
      this.colors = [];
      this.background = '';
      return;
    }

    this.colors = this.resolveColors( product );
    this.background = product.backgroundImages?.length
      ? product.backgroundImages[ 0 ].src
      : '';
    this.category = product.category || this.productLine?.name || '';
    this.appearance = product.appearance || '';
    this.plainDescription = ( product.description || '' ).replace( /<[^>]*>/g, '' );
    this.isDark = ( product.appearance || '' ).toLowerCase() === 'dark';
    this.tags = ( product.tags || [] ).slice( 0, 3 );
    this.editorialLabel = product.editorialLabel || '';
    this.isFree = isThemeFree( product );
    this.platformLogos = ( product.platforms || [] )
      .map( p => p.info?.logo )
      .filter( ( logo ): logo is string => !!logo );

    this.screenshots = ( product.platforms || [] )
      .map( p => p.screenshot?.src )
      .filter( ( src ): src is string => !!src )
      .slice( 0, 5 );

    // Poster composition state, same recipe as theme-card.component: posterColorSet
    // (appearance-only, never a LINE override) drives the scrim tone, designToken
    // foregrounds win for the text, tags collapse to a quiet "a · b · c" line.
    const set = posterColorSet( product.appearance );
    this.posterPanel = set.bg;
    this.posterPanel0 = set.bg + '00';
    this.posterFg = product.designTokens?.primaryForeground || set.fg;
    this.posterFgDim = product.designTokens?.secondaryForeground || set.fgDim;
    this.posterTags = ( product.tags || [] ).slice( 0, 3 ).join( ' · ' );
  }

  /** Build the swatch column from the five `compactPalette` (the colours shown
   * in the site's color circles), falling back to `palette`. Deduped,
   * uppercased, capped at `MAX_COLORS`. */
  private resolveColors( product: Product ): string[] {
    const source = product.compactPalette?.length
      ? product.compactPalette
      : ( product.palette || [] );

    const seen = new Set<string>();
    const out: string[] = [];
    for ( const hex of source ) {
      const value = hex.toUpperCase();
      if ( !seen.has( value ) ) {
        seen.add( value );
        out.push( value );
      }
    }

    return out.slice( 0, this.MAX_COLORS );
  }
}
